import { Component } from '@angular/core'
import { CoreService } from '../../../../services'
import { DialogService } from 'primeng/dynamicdialog'
import * as XLSX from 'xlsx'
import { InvalidListComponent } from '../../dialogs/invalid-list/invalid-list.component'
import { Depot, Export } from 'depoto-core/src/entities'
import { ProductMovePack } from 'depoto-core/src/entities/ProductMovePack'
import { Emittable, Emitter } from '@ngxs-labs/emitter'
import { PlugplayState } from '../../states/plugplay-state.service'
import { OptionsConfig } from '../../../../configs'
import { TranslateService } from '@ngx-translate/core'
import { ProductMove } from 'depoto-core/src/entities/ProductMove'
import { Router } from '@angular/router'
import { FileUtilService } from '../../../../utils/file-util.service'
import { isDateValid } from '../../../../utils'

@Component({
  selector: 'app-step6',
  templateUrl: './step6.component.html',
  styleUrls: ['./step6.component.scss'],
})
export class Step6Component {
  @Emitter(PlugplayState.setActive)
  private readonly setActive: Emittable<number>
  @Emitter(PlugplayState.setHighestLastStepCompleted)
  public readonly setHighestLastStepCompleted: Emittable<number>
  @Emitter(PlugplayState.setHighestStepVisited)
  private readonly setHighestStepVisited: Emittable<number>
  private supplierList: Array<{ id: null; name: null; externalId: null }>
  public showSkipMessage: boolean
  public showProgress: boolean
  private xlsxData: any
  public depots: Depot[] = []
  private productMap = new Map<number, { product: any; supplierId: number }>()
  public progressValue = 0
  exportLoading = true
  exportUrl = ''
  file: File
  arrayBuffer: any
  showStart = true
  errorMap = new Map<number, Array<string>>()
  itemCount: number
  public showImport = true
  public isImportInProgress = false
  showResultPage = false

  constructor(
    public core: CoreService,
    public dialogService: DialogService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly fileUtil: FileUtilService,
  ) {
    this.setActive.emit(5)
    this.setHighestStepVisited.emit(5)

    this.core.services.supplier
      .getList({ page: 0, sort: 'id', direction: 'DESC' }, { id: null, name: null, externalId: null })
      .then(response => (this.supplierList = response.items))
    this.depots = this.core.lists.depots.filter(
      depot => depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id),
    )
    this.core.services.export
      .create(new Export({ type: 'hs-wizard-product-depots' }), { id: null, url: null, file: { mimeType: null } })
      .then(value => this.fetchExport(value.id))
  }

  private fetchExport(id: number) {
    this.core.services.export.getById(id, { id: null, url: null }).then(response => {
      if (response.url) {
        this.exportLoading = false
        this.exportUrl = response.url
      } else {
        setTimeout(this.fetchExport.bind(this, id), 1000)
      }
    })
  }

  async downloadFile() {
    await this.fileUtil.downloadFile(this.exportUrl, 'stock-import.xlsx')
  }

  async selected(event) {
    this.errorMap.clear()
    this.file = event.addedFiles
      .filter(f => f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      .pop()
    if (this.file) {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(this.file)
      fileReader.onload = async e => {
        this.arrayBuffer = fileReader.result
        const data = new Uint8Array(this.arrayBuffer)
        const arr = []
        for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i])
        const bstr = arr.join('')
        const workbook = XLSX.read(bstr, { type: 'binary', cellText: false, cellDates: true })
        const first_sheet_name = workbook.SheetNames[0]
        const header: Array<string> = XLSX.utils
          .sheet_to_json(workbook.Sheets[first_sheet_name], { header: 1 })
          .shift() as Array<string>
        this.xlsxData = XLSX.utils
          .sheet_to_json(workbook.Sheets[first_sheet_name], { raw: false, dateNF: 'yyyy-mm-dd' })
          .map(row => {
            const temp = {}
            header.forEach((key, index) => (temp[index] = row[key]))
            return temp
          })
        this.itemCount = this.xlsxData.length

        await this.validate(this.xlsxData)

        this.showStart = false
        this.showSkipMessage = true
      }
    }
  }

  private async validate(list: Array<any>) {
    const listP = []
    list.forEach((value, index) => {
      listP.push(this.errorType(value, index))
    })
    Promise.all(listP).then(list => {
      list
        .filter(result => result.error[0] !== ERROR_TYPE_MOVE_PACK.IS_EVERYTHING_OK)
        .forEach(value => this.errorMap.set(value.line, value.error))
    })
  }

  private async errorType(row: any, line: number): Promise<{ error: Array<ERROR_TYPE_MOVE_PACK>; line: number }> {
    const supplier = this.supplierList.filter(value => value.name === row[COLUMNS_MOVE_PACK.SUPPLIER])
    const productList =
      (
        await this.core.services.product.getList(
          { filters: { name: row[COLUMNS_MOVE_PACK.NAME] } },
          { id: null, supplier: { id: null } },
        )
      )?.items || []
    const errors = []
    if (!row[COLUMNS_MOVE_PACK.NAME] || productList.length === 0) {
      errors.push(ERROR_TYPE_MOVE_PACK.NAME)
    }
    if (
      (!!row[COLUMNS_MOVE_PACK.AMOUNT] && isNaN(row[COLUMNS_MOVE_PACK.AMOUNT])) ||
      row[COLUMNS_MOVE_PACK.AMOUNT] === undefined
    ) {
      errors.push(ERROR_TYPE_MOVE_PACK.AMOUNT)
    }
    if (productList?.length !== 0 && supplier.length !== 0) {
      this.productMap.set(line, { product: productList[0], supplierId: supplier[0]?.id })
    }
    if (
      (supplier.length === 0 && !!row[COLUMNS_MOVE_PACK.SUPPLIER]) ||
      typeof row[COLUMNS_MOVE_PACK.SUPPLIER] === 'undefined'
    ) {
      errors.push(ERROR_TYPE_MOVE_PACK.SUPPLIER)
    }
    if (isNaN(row[COLUMNS_MOVE_PACK.PRICE]) && !!row[COLUMNS_MOVE_PACK.PRICE]) {
      errors.push(ERROR_TYPE_MOVE_PACK.PRICE)
    }

    if (row[COLUMNS_MOVE_PACK.DEPOT]) {
      if (!this.depots.find(value => value.name === row[COLUMNS_MOVE_PACK.DEPOT])) {
        errors.push(ERROR_TYPE_MOVE_PACK.DEPOT)
      }
    } else {
      errors.push(ERROR_TYPE_MOVE_PACK.DEPOT)
    }

    if (row[COLUMNS_MOVE_PACK.EXPIRATION]) {
      if (!isDateValid(row[COLUMNS_MOVE_PACK.EXPIRATION])) {
        errors.push(ERROR_TYPE_MOVE_PACK.EXPIRATION)
      }
    }

    return errors.length === 0 ? { error: [ERROR_TYPE_MOVE_PACK.IS_EVERYTHING_OK], line } : { error: errors, line }
  }

  public import(): void {
    this.showProgress = true
    this.showSkipMessage = false
    const result = this.transformData(this.xlsxData)
    const groups = this.splitIntoPilesAccordingToDepot(result)
    this.save(groups)
  }

  private transformData(data: Array<any>): Array<any> {
    const pom = data
      .map((row, index) => {
        const error = this.errorMap.get(index)
        if (
          !(
            error?.includes(ERROR_TYPE_MOVE_PACK.NAME) ||
            error?.includes(ERROR_TYPE_MOVE_PACK.AMOUNT) ||
            error?.includes(ERROR_TYPE_MOVE_PACK.DEPOT) ||
            error?.includes(ERROR_TYPE_MOVE_PACK.SUPPLIER) ||
            error?.includes(ERROR_TYPE_MOVE_PACK.EXPIRATION)
          )
        ) {
          const item = this.productMap.get(index)
          const result: any = {
            product: { id: item.product.id },
            supplier:
              item.supplierId ||
              item.product.supplierId ||
              this.supplierList.find(value => value.externalId === 'henryschein')?.id,
            purchasePrice: row[COLUMNS_MOVE_PACK.PRICE],
            amount: error?.includes(ERROR_TYPE_MOVE_PACK.AMOUNT) ? null : row[COLUMNS_MOVE_PACK.AMOUNT],
            depotTo: this.depots.find(value => value.name === row[COLUMNS_MOVE_PACK.DEPOT]).id,
          }
          if (row[COLUMNS_MOVE_PACK.EXPIRATION]) {
            result.expirationDate = `${row[COLUMNS_MOVE_PACK.EXPIRATION]}`
          }
          if (row[COLUMNS_MOVE_PACK.BATCH]) {
            result.batch = `${row[COLUMNS_MOVE_PACK.BATCH]}`
          }
          return result
        }
      })
      .filter(value => value)
    return pom
  }

  showInvalidData() {
    const result = new Array<{ line: number; error: string }>()
    this.errorMap.forEach((value, key) => result.push({ line: key, error: value[0] }))
    this.dialogService.open(InvalidListComponent, {
      header: this.translateService.instant('wizard.invalid-dialog.title'),
      data: {
        errorList: result,
      },
      style: {
        width: '600px',
        height: 'auto',
      },
    })
  }

  private splitIntoPilesAccordingToDepot(items: Array<ProductMove>): Map<number, Array<ProductMove>> {
    const groupMap = new Map<number, Array<ProductMove>>()
    items.forEach(item => {
      const record = groupMap.get(item.depotTo)
      if (record) {
        groupMap.set(item.depotTo, [...record, item])
      } else {
        groupMap.set(item.depotTo, [item])
      }
    })
    return groupMap
  }

  private save(result: Map<number, Array<ProductMove>>) {
    this.progressValue = 0
    const promList = []
    result.forEach((value, key) => {
      const pmp = new ProductMovePack({
        moves: value,
        type: { id: 'in' },
      })
      pmp.moves.forEach(m => (m.purchaseCurrency = undefined))
      promList.push(() =>
        this.core.services.productMovePack.create(pmp).then(_ => {
          this.progressValue += 1
        }),
      )
    })
    this.handlePromises(promList)
  }

  async handlePromises(promises: any) {
    for (const promise of promises) {
      await promise()
    }
    this.showImport = !this.showImport
    this.isImportInProgress = false
    this.showResultPage = true
  }

  back(showImport: boolean) {
    if (showImport && !this.showResultPage) {
      this.router.navigate(['/plugplay/step-5'])
    } else {
      this.showStart = true
      this.showProgress = false
      this.showImport = !this.showImport
      this.showResultPage = false
      this.file = null
    }
  }

  next(showImport: boolean) {
    if (showImport) {
      if (this.showResultPage) {
        this.showImport = !this.showImport
      } else {
        this.router.navigate(['/plugplay/step-7/check'])
        this.setHighestLastStepCompleted.emit(5)
      }
    } else {
      this.setHighestLastStepCompleted.emit(5)
      this.router.navigate(['/plugplay/step-7/check'])
    }
  }
}

enum ERROR_TYPE_MOVE_PACK {
  NAME = 'wizard.step-6.error.name',
  AMOUNT = 'wizard.step-6.error.amount',
  EXPIRATION = 'wizard.step-6.error.expiration',
  SUPPLIER = 'wizard.step-6.error.supplier',
  PRICE = 'wizard.step-6.error.price',
  IS_EVERYTHING_OK = 'true',
  DEPOT = 'wizard.step-6.error.depot',
}

enum COLUMNS_MOVE_PACK {
  NAME = 0,
  AMOUNT = 1,
  BATCH = 3,
  EXPIRATION = 4,
  SUPPLIER = 5,
  PRICE = 6,
  DEPOT = 2,
}
